import { portalApi } from '../../../api';
import { modifyArrayItemAt, replaceArrayItemAt } from '../../../utils/arrayUtil';
import { debounceAction } from '../../../utils/debounce';
import { createErrorToast, createSuccessToast, createToast } from '../app';
import { getCompanyDepartments, getCompanyStats, selectCompanyDepartments, selectCompanyId } from '../company';
import { selectUserId, updateCurrentUser } from '../user';

import { formatErrorMessage } from './util';

const ADMIN_LIST_USERS = 'ADMIN_LIST_USERS';
const ADMIN_LIST_USERS_SUCCESS = 'ADMIN_LIST_USERS_SUCCESS';
const ADMIN_LIST_USERS_FAILURE = 'ADMIN_LIST_USERS_FAILURE';

export const adminPageListUsers = companyId => async (dispatch, getState) => {
  const currentCompanyId = companyId ?? selectCompanyId(getState());
  dispatch({ type: ADMIN_LIST_USERS, currentCompanyId });

  try {
    const data = await portalApi.getCompanyUsers(currentCompanyId);
    const dataExcludingIntegrationViewers = data.filter(user => !user.is_procore_viewer);

    dispatch({ type: ADMIN_LIST_USERS_SUCCESS, data: dataExcludingIntegrationViewers });
  } catch (error) {
    dispatch({ type: ADMIN_LIST_USERS_FAILURE, error });
  }
};

const ADMIN_NEW_USER_DATA = 'ADMIN_NEW_USER_DATA';

export const adminPageNewUserData = newUserData => async dispatch => {
  dispatch({ type: ADMIN_NEW_USER_DATA, newUserData });
};

const ADMIN_LIST_ROLES = 'ADMIN_LIST_ROLES';
const ADMIN_LIST_ROLES_SUCCESS = 'ADMIN_LIST_ROLES_SUCCESS';
const ADMIN_LIST_ROLES_FAILURE = 'ADMIN_LIST_ROLES_FAILURE';

export const adminPageListRoles = companyId => async dispatch => {
  dispatch({ type: ADMIN_LIST_ROLES, companyId });

  try {
    const roles = await portalApi.listCompanyRoles(companyId);
    dispatch({ type: ADMIN_LIST_ROLES_SUCCESS, roles });
  } catch (error) {
    dispatch({ type: ADMIN_LIST_ROLES_FAILURE, error });
  }
};

export const ADMIN_COMPANY_CODES = 'ADMIN_COMPANY_CODES';
export const ADMIN_COMPANY_CODES_SUCCESS = 'ADMIN_COMPANY_CODES_SUCCESS';
export const ADMIN_COMPANY_CODES_FAILURE = 'ADMIN_COMPANY_CODES_FAILURE';

export const adminPageCompanyCodes = companyId => async dispatch => {
  dispatch({ type: ADMIN_COMPANY_CODES, companyId });

  try {
    const codes = await portalApi.getCompanyCodes(companyId);
    dispatch({ type: ADMIN_COMPANY_CODES_SUCCESS, codes });
  } catch (error) {
    dispatch({ type: ADMIN_COMPANY_CODES_FAILURE, error });
  }
};

export const ADMIN_CREATE_USER = 'ADMIN_CREATE_USER';
export const ADMIN_CREATE_USER_SUCCESS = 'ADMIN_CREATE_USER_SUCCESS';
export const ADMIN_CREATE_USER_FAILURE = 'ADMIN_CREATE_USER_FAILURE';

export const ADMIN_CREATE_USER_ROLES = 'ADMIN_CREATE_USER_ROLES';
export const ADMIN_CREATE_USER_ROLES_SUCCESS = 'ADMIN_CREATE_USER_ROLES_SUCCESS';
export const ADMIN_CREATE_USER_ROLES_FAILURE = 'ADMIN_CREATE_USER_ROLES_FAILURE';

export const adminPageCreateUser = userData => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  const userInfo = {
    user_first_name: userData.userFirstName,
    user_last_name: userData.userLastName,
    user_email: userData.userEmail,
    external_id: userData.externalId,
    ...(userData.userPhone && { user_phone: userData.userPhone }),
    ...(userData.supervisorId && { supervisor_id: userData.supervisorId })
  };

  const userRoles = userData.roles;

  let departmentRoles = [];
  if (userRoles) {
    userRoles.forEach(role => {
      const { roleId } = role;
      role.departmentIds.forEach(deptId => {
        const departmentRole = { deptId, roleId };
        departmentRoles = [...departmentRoles, departmentRole];
      });
    });
  }

  dispatch({ type: ADMIN_CREATE_USER, companyId, userInfo });

  try {
    const newUser = await portalApi.createUser(companyId, userInfo);
    dispatch({ type: ADMIN_CREATE_USER_SUCCESS, newUser });

    if (newUser && userData.companyRole) {
      const defaultCompanyRoleId = newUser.roles[0].id;
      dispatch(adminPageUpdateUserCompanyRole(newUser.user_id, defaultCompanyRoleId, userData.companyRole));
    }

    dispatch(adminPageListUsers(companyId));

    if (userRoles) {
      const userDepartmentRoles = Promise.all(
        departmentRoles.map(role => portalApi.createUserDepartmentRole(
          role.deptId,
          newUser.user_id,
          role.roleId
        ))
      );

      Promise.all(userDepartmentRoles)
        .then(response => {
          const finalRes = response[1].length - 1;
          const newUserRoles = response[1][finalRes];

          dispatch({ type: ADMIN_CREATE_USER_ROLES_SUCCESS, newUserRoles });
          dispatch(adminPageListUsers(companyId));
        }).catch(error => error.message);
    }

    dispatch(createSuccessToast({ title: 'User created successfully!' }));
  } catch (error) {
    dispatch({ type: ADMIN_CREATE_USER_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! User was not created because: ',
      description: formatErrorMessage(error.body),
      error
    }));

    dispatch(adminPageListUsers(companyId));
  }
};

export const ADMIN_UPDATE_USER = 'ADMIN_UPDATE_USER';
export const ADMIN_UPDATE_USER_SUCCESS = 'ADMIN_UPDATE_USER_SUCCESS';
export const ADMIN_UPDATE_USER_FAILURE = 'ADMIN_UPDATE_USER_FAILURE';

export const adminPageUpdateUserInfo = (userId, userData) => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());
  const currentUserId = selectUserId(getState());

  dispatch({ type: ADMIN_UPDATE_USER, userId, userData });

  try {
    const updatedUser = await portalApi.updateUser(userId, userData);
    dispatch({ type: ADMIN_UPDATE_USER_SUCCESS, updatedUser });

    if (updatedUser.user_id === currentUserId) {
      dispatch(updateCurrentUser(updatedUser));
    }

    dispatch(createSuccessToast({ title: 'User updated successfully!' }));
    dispatch(adminPageListUsers(companyId));
  } catch (error) {
    dispatch({ type: ADMIN_UPDATE_USER_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh, something went wrong.',
      description: 'Please try again or contact support',
      error
    }));
  }
};

export const ADMIN_UPDATE_COMPANY_ROLE = 'ADMIN_UPDATE_COMPANY_ROLE';
export const ADMIN_UPDATE_COMPANY_ROLE_SUCCESS = 'ADMIN_UPDATE_COMPANY_ROLE_SUCCESS';
export const ADMIN_UPDATE_COMPANY_ROLE_FAILURE = 'ADMIN_UPDATE_COMPANY_ROLE_FAILURE';

export const adminPageUpdateUserCompanyRole = (userId, currentRoleId, newRoleId) => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  dispatch({
    type: ADMIN_UPDATE_COMPANY_ROLE,
    companyId,
    userId,
    currentRoleId,
    newRoleId
  });

  try {
    const user = await portalApi.createUserCompanyRole(companyId, userId, newRoleId);
    dispatch({ type: ADMIN_UPDATE_COMPANY_ROLE_SUCCESS, user });
  } catch (error) {
    dispatch({ type: ADMIN_UPDATE_COMPANY_ROLE_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! Something went wrong.',
      description: 'User\'s company role was not created. Please edit the user to update the role.'
      + 'If the issue continues, reach out to support@pinsadvantage.com.',
      error
    }));
  }
};

export const ADMIN_DELETE_COMPANY_ROLE = 'ADMIN_DELETE_COMPANY_ROLE';
export const ADMIN_DELETE_COMPANY_ROLE_SUCCESS = 'ADMIN_DELETE_COMPANY_ROLE_SUCCESS';
export const ADMIN_DELETE_COMPANY_ROLE_FAILURE = 'ADMIN_DELETE_COMPANY_ROLE_FAILURE';

export const adminPageDeleteUserCompanyRole = (userId, roleId) => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  dispatch({
    type: ADMIN_DELETE_COMPANY_ROLE,
    companyId,
    userId,
    roleId
  });

  try {
    const user = await portalApi.deleteUserCompanyRole(companyId, userId, roleId);
    dispatch({ type: ADMIN_DELETE_COMPANY_ROLE_SUCCESS, user });
  } catch (error) {
    dispatch({ type: ADMIN_DELETE_COMPANY_ROLE_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! Something went wrong.',
      description: 'User\'s company role was not created. Please edit the user to update the role.'
          + 'If the issue continues, reach out to support@pinsadvantage.com.',
      error
    }));
  }
};

export const adminPageCreateUserDepartmentRole = (roles, userId) => async (dispatch, getState) => {
  let departmentRoles = [];
  const companyId = selectCompanyId(getState());

  roles.forEach(role => {
    const { roleId } = role;
    role.departmentIds.forEach(deptId => {
      const departmentRole = { deptId, roleId };
      departmentRoles = [...departmentRoles, departmentRole];
    });
  });

  dispatch({ type: ADMIN_CREATE_USER_ROLES, departmentRoles, userId });

  try {
    await Promise.all(departmentRoles.map(deptRole => portalApi.createUserDepartmentRole(
      deptRole.deptId,
      userId,
      deptRole.roleId
    )));

    const user = await portalApi.getCompanyUser(companyId, userId);
    dispatch({ type: ADMIN_CREATE_USER_ROLES_SUCCESS, user });
    return true;
  } catch (error) {
    dispatch({ type: ADMIN_CREATE_USER_ROLES_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! Something went wrong.',
      description: 'User\'s department role(s) not created. Please edit the user to update the role(s).'
      + 'If the issue continues, reach out to support@pinsadvantage.com.',
      error
    }));

    return false;
  }
};

export const ADMIN_DELETE_DEPARTMENT_ROLE = 'ADMIN_DELETE_DEPARTMENT_ROLE';
export const ADMIN_DELETE_DEPARTMENT_ROLE_SUCCESS = 'ADMIN_DELETE_DEPARTMENT_ROLE_SUCCESS';
export const ADMIN_DELETE_DEPARTMENT_ROLE_FAILURE = 'ADMIN_DELETE_DEPARTMENT_ROLE_FAILURE';

export const adminPageDeleteDepartmentRole = (deletedRole, userId) => async (dispatch, getState) => {
  dispatch({ type: ADMIN_DELETE_DEPARTMENT_ROLE, deletedRole, userId });

  const { roleId } = deletedRole;
  const { departmentIds } = deletedRole;
  const companyId = selectCompanyId(getState());

  try {
    await Promise.all(departmentIds.map(deptId => portalApi.deleteUserDepartmentRole(deptId, userId, roleId)));
    const user = await portalApi.getCompanyUser(companyId, userId);
    dispatch({ type: ADMIN_DELETE_DEPARTMENT_ROLE_SUCCESS, user });
  } catch (error) {
    dispatch({ type: ADMIN_DELETE_DEPARTMENT_ROLE_FAILURE, error });
  }
};

export const ADMIN_PAGE_DEACTIVATE_USER = 'ADMIN_PAGE_DEACTIVATE_USER';
export const ADMIN_PAGE_DEACTIVATE_USER_SUCCESS = 'ADMIN_PAGE_DEACTIVATE_USER_SUCCESS';
export const ADMIN_PAGE_DEACTIVATE_USER_FAILURE = 'ADMIN_PAGE_DEACTIVATE_USER_FAILURE';

export const adminPageDeactivateUser = userId => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  dispatch({ type: ADMIN_PAGE_DEACTIVATE_USER, companyId, userId });

  try {
    const inactiveUser = await portalApi.deactivateUser(userId, companyId);
    dispatch(createSuccessToast({ title: 'User Successfully Deactivated' }));
    dispatch({ type: ADMIN_PAGE_DEACTIVATE_USER_SUCCESS, inactiveUser });
  } catch (error) {
    dispatch({ type: ADMIN_PAGE_DEACTIVATE_USER_FAILURE, error });
    dispatch(createErrorToast({ title: 'Uh oh, something went wrong', description: 'User was not deactivated', error }));
  }
};

export const ADMIN_PAGE_ACTIVATE_USER = 'ADMIN_PAGE_ACTIVATE_USER';
export const ADMIN_PAGE_ACTIVATE_USER_SUCCESS = 'ADMIN_PAGE_ACTIVATE_USER_SUCCESS';
export const ADMIN_PAGE_ACTIVATE_USER_FAILURE = 'ADMIN_PAGE_ACTIVATE_USER_FAILURE';

export const adminPageActivateUser = userId => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  dispatch({ type: ADMIN_PAGE_ACTIVATE_USER, companyId, userId });

  try {
    const activeUser = await portalApi.activateUser(companyId, userId);
    dispatch({ type: ADMIN_PAGE_ACTIVATE_USER_SUCCESS, activeUser });
    dispatch(createSuccessToast({ title: 'User Successfully Reactivated' }));
  } catch (error) {
    dispatch({ type: ADMIN_PAGE_ACTIVATE_USER_FAILURE, error });
    dispatch(createErrorToast({ title: 'Uh oh, something went wrong', description: 'User was not reactivated', error }));
  }
};

export const ADMIN_PAGE_CREATE_DEPARTMENT = 'ADMIN_PAGE_CREATE_DEPARTMENT';
export const ADMIN_PAGE_CREATE_DEPARTMENT_SUCCESS = 'ADMIN_PAGE_CREATE_DEPARTMENT_SUCCESS';
export const ADMIN_PAGE_CREATE_DEPARTMENT_FAILURE = 'ADMIN_PAGE_CREATE_DEPARTMENT_FAILURE';

export const adminPageCreateDepartment = departmentData => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  const departmentInfo = {
    department_name: departmentData.departmentName,
    owner_id: departmentData.ownerId,
    dept_address_1: departmentData.deptAddress1 || null,
    dept_address_2: departmentData.deptAddress2 || null,
    dept_city: departmentData.city || null,
    dept_state: departmentData.state || null,
    dept_zip: departmentData.deptZip || null
  };

  dispatch({ type: ADMIN_PAGE_CREATE_DEPARTMENT, companyId, departmentInfo });

  dispatch(createToast({ title: 'Creating Department...' }));

  try {
    const newDepartment = await portalApi.createDepartment(companyId, departmentInfo);
    dispatch({ type: ADMIN_PAGE_CREATE_DEPARTMENT_SUCCESS, newDepartment });

    await dispatch(getCompanyDepartments());

    dispatch(createSuccessToast({ title: 'Department created successfully!' }));
  } catch (error) {
    dispatch({ type: ADMIN_PAGE_CREATE_DEPARTMENT_FAILURE, error });
    dispatch(createErrorToast({ title: 'Uh oh, something went wrong.', description: 'Please contact support for assistance.', error }));
  }
};

export const ADMIN_PAGE_RESEND_INVITE_EMAIL = 'ADMIN_PAGE_RESEND_INVITE_EMAIL';
export const ADMIN_PAGE_RESEND_INVITE_EMAIL_SUCCESS = 'ADMIN_PAGE_RESEND_INVITE_EMAIL_SUCCESS';
export const ADMIN_PAGE_RESEND_INVITE_EMAIL_FAILURE = 'ADMIN_PAGE_RESEND_INVITE_EMAIL_FAILURE';

export const resendUserInviteEmail = userId => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());

  dispatch({ type: ADMIN_PAGE_RESEND_INVITE_EMAIL, userId, companyId });

  try {
    const response = await portalApi.resendUserInviteEmail(userId, companyId);
    if (response === 200) {
      dispatch(createSuccessToast({ title: 'Invite Email Resent Successfully!', duration: 5000 }));
    } else {
      dispatch({ type: ADMIN_PAGE_RESEND_INVITE_EMAIL_FAILURE, response });
      dispatch(createErrorToast({
        title: 'Uh oh! Invite email was not sent.',
        description: 'If the issue continues, reach out to support@pinsadvantage.com.'
      }));

      dispatch(adminPageListUsers(companyId));
      return { success: false, message: response };
    }

    dispatch({ type: ADMIN_PAGE_RESEND_INVITE_EMAIL_SUCCESS, response });
  } catch (error) {
    dispatch({ type: ADMIN_PAGE_RESEND_INVITE_EMAIL_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! Invite email was not sent.',
      description: 'If the issue continues, reach out to support@pinsadvantage.com.',
      error
    }));

    dispatch(adminPageListUsers(companyId));
    return { success: false, message: error.message };
  }

  return { success: true };
};

export const ADMIN_PAGE_UPDATE_DEPARTMENT = 'ADMIN_PAGE_UPDATE_DEPARTMENT';
export const ADMIN_PAGE_UPDATE_DEPARTMENT_SUCCESS = 'ADMIN_PAGE_UPDATE_DEPARTMENT_SUCCESS';
export const ADMIN_PAGE_UPDATE_DEPARTMENT_FAILURE = 'ADMIN_PAGE_UPDATE_DEPARTMENT_FAILURE';

export const adminPageUpdateDepartment = (departmentId, departmentData) => async dispatch => {
  const departmentInfo = {
    department_name: departmentData.departmentName,
    owner_id: departmentData.ownerId,
    dept_address_1: departmentData.deptAddress1 || null,
    dept_address_2: departmentData.deptAddress2 || null,
    dept_city: departmentData.city || null,
    dept_state: departmentData.state || null,
    dept_zip: departmentData.deptZip || null
  };

  dispatch({ type: ADMIN_PAGE_UPDATE_DEPARTMENT, departmentId, departmentInfo });

  dispatch(createToast({ title: 'Updating Department...' }));

  try {
    const updatedDepartment = await portalApi.updateDepartment(departmentId, departmentInfo);
    dispatch({ type: ADMIN_PAGE_UPDATE_DEPARTMENT_SUCCESS, updatedDepartment });

    await dispatch(getCompanyDepartments());

    dispatch(createSuccessToast({ title: 'Department Updated!', description: 'You\'ll see your changes throughout your account momentarily.' }));
  } catch (error) {
    dispatch({ type: ADMIN_PAGE_UPDATE_DEPARTMENT_FAILURE, error });
    dispatch(createErrorToast({ title: 'Uh oh, something went wrong.', description: 'Please contact support for assistance.', error }));
  }
};

export const ADMIN_PAGE_UPDATE_FILTERS = 'ADMIN_PAGE_UPDATE_FILTERS';
export const adminPageUpdateFilters = (filterName, selectedValue) => dispatch => {
  dispatch({ type: ADMIN_PAGE_UPDATE_FILTERS, filterName, selectedValue });
};

export const ADMIN_PAGE_UPDATE_SEARCH = 'ADMIN_PAGE_UPDATE_SEARCH';
export const adminPageUpdateSearch = debounceAction((name, searchValue) => dispatch => {
  dispatch({ type: ADMIN_PAGE_UPDATE_SEARCH, name, searchValue });
});

const MOVE_DEPARTMENT_DATA = 'MOVE_DEPARTMENT_DATA';
export const moveDepartmentData = departmentData => async dispatch => {
  dispatch({ type: MOVE_DEPARTMENT_DATA, departmentData });
};

const MOVE_DEPARTMENT_DATA_SKIPPED = 'MOVE_DEPARTMENT_DATA_SKIPPED';
export const skipMoveDepartmentData = () => async dispatch => {
  dispatch({ type: MOVE_DEPARTMENT_DATA_SKIPPED });
};

const RESET_MOVE_DEPARTMENT_DATA = 'RESET_MOVE_DEPARTMENT_DATA';
export const resetMoveDepartmentData = () => async dispatch => {
  dispatch({ type: RESET_MOVE_DEPARTMENT_DATA });
};

const RESET_ADMIN_PAGE_CURRENT_DEPARTMENT = 'RESET_ADMIN_PAGE_CURRENT_DEPARTMENT';
export const resetAdminPageCurrentDepartment = () => async dispatch => {
  dispatch({ type: RESET_ADMIN_PAGE_CURRENT_DEPARTMENT });
};

const ADMIN_PAGE_CURRENT_DEPARTMENT = 'ADMIN_PAGE_CURRENT_DEPARTMENT';
export const adminPageSetCurrentDepartment = department => async dispatch => {
  dispatch({ type: ADMIN_PAGE_CURRENT_DEPARTMENT, department });
};

const TRANSFER_DEPARTMENT = 'TRANSFER_DEPARTMENT';
const TRANSFER_DEPARTMENT_SUCCESS = 'TRANSFER_DEPARTMENT_SUCCESS';
const TRANSFER_DEPARTMENT_FAILURE = 'TRANSFER_DEPARTMENT_FAILURE';

export const transferDepartment = () => async (dispatch, getState) => {
  const { fromDepartmentId, toDepartmentId } = getState().adminPage.moveDepartmentData;
  const companyId = selectCompanyId(getState());
  dispatch({ type: TRANSFER_DEPARTMENT, fromDepartmentId });

  try {
    const response = await portalApi.transferDepartment(fromDepartmentId, toDepartmentId);
    const departmentTo = selectCompanyDepartments(getState())
      .filter(companyDepartment => companyDepartment.departmentId === toDepartmentId)?.[0];
    if (response === 200) {
      dispatch({ type: TRANSFER_DEPARTMENT_SUCCESS, response });
      dispatch(getCompanyDepartments(companyId));
      dispatch(getCompanyStats(companyId));
      dispatch(createSuccessToast({
        title: `Success! Data has been transferred to ${departmentTo.departmentName}.`,
        duration: 5000
      }));
    } else {
      dispatch({ type: TRANSFER_DEPARTMENT_FAILURE, response });
      dispatch(createErrorToast({
        title: 'Uh oh! transfer failed.',
        description: 'If the issue continues, reach out to support@pinsadvantage.com.'
      }));

      dispatch(getCompanyDepartments(companyId));
      dispatch(getCompanyStats(companyId));
      return { success: false, message: response };
    }
  } catch (error) {
    dispatch({ type: TRANSFER_DEPARTMENT_FAILURE, error });
    return { success: false, message: error.message };
  }

  return { success: true };
};

export const ARCHIVE_DEPARTMENT = 'ARCHIVE_DEPARTMENT ';
export const ARCHIVE_DEPARTMENT_SUCCESS = 'ARCHIVE_DEPARTMENT_SUCCESS';
export const ARCHIVE_DEPARTMENT_FAILURE = 'ARCHIVE_DEPARTMENT_FAILURE';

export const archiveDepartment = departmentId => async (dispatch, getState) => {
  dispatch({ type: ARCHIVE_DEPARTMENT, departmentId });
  const companyId = selectCompanyId(getState());
  const department = selectAdminPageCurrentDepartmentData(getState());

  try {
    const inactiveDepartment = await portalApi.archiveDepartment(departmentId);
    dispatch({ type: ARCHIVE_DEPARTMENT_SUCCESS, inactiveDepartment });
    dispatch(getCompanyDepartments(companyId));
    dispatch(getCompanyStats(companyId));
    dispatch(createSuccessToast({
      title: `Success! ${department.departmentName} has been archived`,
      duration: 5000
    }));
  } catch (error) {
    dispatch({ type: ARCHIVE_DEPARTMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! Archive failed',
      description: 'If the issue continues, reach out to support@pinsadvantage.com.',
      error
    }));

    dispatch(getCompanyDepartments(companyId));
    dispatch(getCompanyStats(companyId));
  }
};

export const REACTIVATE_DEPARTMENT = 'REACTIVATE_DEPARTMENT';
export const REACTIVATE_DEPARTMENT_SUCCESS = 'REACTIVATE_DEPARTMENT_SUCCESS';
export const REACTIVATE_DEPARTMENT_FAILURE = 'REACTIVATE_DEPARTMENT_FAILURE';

export const reactivateDepartment = departmentId => async (dispatch, getState) => {
  dispatch({ type: REACTIVATE_DEPARTMENT, departmentId });
  const companyId = selectCompanyId(getState());
  const department = selectAdminPageCurrentDepartmentData(getState());

  try {
    const reactivatedDepartment = await portalApi.activateDepartment(departmentId);
    dispatch({ type: REACTIVATE_DEPARTMENT_SUCCESS, reactivatedDepartment });
    dispatch(getCompanyDepartments(companyId));
    dispatch(getCompanyStats(companyId));
    dispatch(createSuccessToast({
      title: `Success! ${department.departmentName} has been reactivated`,
      duration: 5000
    }));
  } catch (error) {
    dispatch({ type: REACTIVATE_DEPARTMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! reactivation failed',
      description: 'If the issue continues, reach out to support@pinsadvantage.com.',
      error
    }));

    dispatch(getCompanyDepartments(companyId));
    dispatch(getCompanyStats(companyId));
  }
};

export const DELETE_DEPARTMENT = 'DELETE_DEPARTMENT';
export const DELETE_DEPARTMENT_SUCCESS = 'DELETE_DEPARTMENT_SUCCESS';
export const DELETE_DEPARTMENT_FAILURE = 'DELETE_DEPARTMENT_FAILURE';

export const deleteDepartment = () => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());
  const { departmentName, departmentId } = selectAdminPageCurrentDepartmentData(getState());
  dispatch({ type: DELETE_DEPARTMENT, departmentId });

  try {
    await portalApi.deleteDepartment(departmentId);
    dispatch({ type: DELETE_DEPARTMENT_SUCCESS });
    dispatch(getCompanyDepartments(companyId));
    dispatch(getCompanyStats(companyId));
    dispatch(createSuccessToast({
      title: `Success! ${departmentName} has been deleted`,
      duration: 5000
    }));
  } catch (error) {
    dispatch({ type: DELETE_DEPARTMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Uh oh! deletion failed',
      description: 'If the issue continues, reach out to support@pinsadvantage.com.',
      error
    }));

    dispatch(getCompanyDepartments(companyId));
    dispatch(getCompanyStats(companyId));
  }
};

/* Default State */
const DEFAULT_STATE = {
  companyId: null,
  users: { isLoading: false },
  codes: [],
  roles: [],
  newUserData: {},
  currentDepartmentData: {},
  moveDepartmentData: {
    success: false,
    skipped: false
  },
  viewUsers: 'Active Users',
  filters: [
    { name: 'user', selected: 'active' },
    { name: 'department', selected: 'active' }
  ],
  search: [
    { name: 'user', value: '' },
    { name: 'department', value: '' }
  ]

};

/* Reducer */
const adminPageReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case ADMIN_LIST_USERS:
      return { ...state, users: { ...state.users, isLoading: true } };
    case ADMIN_LIST_USERS_SUCCESS:
      return { ...state, users: { isLoading: false, data: action.data } };
    case ADMIN_LIST_USERS_FAILURE:
      return { ...state, users: { isLoading: false }, error: action.error };
    case ADMIN_COMPANY_CODES_SUCCESS:
      return { ...state, codes: action.codes };
    case ADMIN_COMPANY_CODES_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_LIST_ROLES_SUCCESS:
      return { ...state, roles: action.roles };
    case ADMIN_LIST_ROLES_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_NEW_USER_DATA:
      return { ...state, newUserData: action.newUserData };
    case MOVE_DEPARTMENT_DATA:
      return { ...state, moveDepartmentData: action.departmentData, success: false, skipped: false };
    case TRANSFER_DEPARTMENT_SUCCESS:
      return { ...state, moveDepartmentData: { ...state.moveDepartmentData, success: true } };
    case MOVE_DEPARTMENT_DATA_SKIPPED:
      return { ...state, moveDepartmentData: { ...state.moveDepartmentData, skipped: true } };
    case ADMIN_CREATE_USER_SUCCESS:
      return { ...state, newUser: action.newUser };
    case ADMIN_CREATE_USER_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_CREATE_USER_ROLES_SUCCESS:
      return {
        ...state,
        users: { ...state.users, data: replaceArrayItemAt(state.users.data, 'userId', action.user.userId, action.user) }
      };
    case ADMIN_CREATE_USER_ROLES_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_UPDATE_USER_SUCCESS:
      return { ...state, updatedUser: action.updatedUser };
    case ADMIN_UPDATE_USER_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_UPDATE_COMPANY_ROLE_SUCCESS:
      return {
        ...state,
        users: { ...state.users, data: replaceArrayItemAt(state.users.data, 'userId', action.user.userId, action.user) }
      };
    case ADMIN_UPDATE_COMPANY_ROLE_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_DELETE_COMPANY_ROLE_SUCCESS:
      return {
        ...state,
        users: { ...state.users, data: replaceArrayItemAt(state.users.data, 'userId', action.user.userId, action.user) }
      };
    case ADMIN_DELETE_COMPANY_ROLE_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_DELETE_DEPARTMENT_ROLE_SUCCESS:
      return {
        ...state,
        users: { ...state.users, data: replaceArrayItemAt(state.users.data, 'userId', action.user.userId, action.user) }
      };
    case ADMIN_DELETE_DEPARTMENT_ROLE_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_PAGE_DEACTIVATE_USER_SUCCESS:
      return { ...state, inactiveUser: action.inactiveUser };
    case ADMIN_PAGE_DEACTIVATE_USER_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_PAGE_ACTIVATE_USER_SUCCESS:
      return { ...state, activeUser: action.activeUser };
    case ADMIN_PAGE_ACTIVATE_USER_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_PAGE_CREATE_DEPARTMENT_SUCCESS:
      return { ...state, newDepartment: action.newDepartment };
    case ADMIN_PAGE_CREATE_DEPARTMENT_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_PAGE_UPDATE_DEPARTMENT_SUCCESS:
      return { ...state, updatedDepartment: action.updatedDepartment };
    case ADMIN_PAGE_UPDATE_DEPARTMENT_FAILURE:
      return { ...state, error: action.error };
    case ADMIN_PAGE_UPDATE_FILTERS:
      return {
        ...state,
        filters: modifyArrayItemAt(
          state.filters,
          'name',
          action.filterName,
          filter => ({ ...filter, selected: action.selectedValue })
        )
      };
    case ADMIN_PAGE_UPDATE_SEARCH:
      return {
        ...state,
        search: modifyArrayItemAt(
          state.search,
          'name',
          action.name,
          search => ({ ...search, value: action.searchValue })
        )
      };
    case ADMIN_PAGE_CURRENT_DEPARTMENT:
      return { ...state, currentDepartmentData: action.department };
    case ADMIN_PAGE_RESEND_INVITE_EMAIL_FAILURE:
      return { ...state, error: action.error };
    case RESET_MOVE_DEPARTMENT_DATA:
      return { ...state, moveDepartmentData: { ...DEFAULT_STATE.moveDepartmentData } };
    case RESET_ADMIN_PAGE_CURRENT_DEPARTMENT:
      return { ...state, currentDepartmentData: { ...DEFAULT_STATE.currentDepartmentData } };
    case TRANSFER_DEPARTMENT_FAILURE:
      return { ...state, error: action.error };
    default:
      return state;
  }
};

/* Selectors */
export const selectAdminPageUsers = state => state.adminPage.users.data;
export const selectAdminPageCodes = state => state.adminPage.codes;
export const selectAdminPageRoles = state => state.adminPage.roles;
export const selectAdminPageNewUserData = state => state.adminPage.newUserData;
export const selectAdminPageCurrentDepartmentData = state => state.adminPage.currentDepartmentData;
export const selectAdminPageFilters = state => state.adminPage.filters;
export const selectAdminPageSearch = state => state.adminPage.search;
export const selectAreUsersLoading = state => state.adminPage.users.isLoading;
export const selectMoveDepartmentData = state => state.adminPage.moveDepartmentData;
export const selectAdminUser = userId => state => state.adminPage.users.data?.find(u => u.userId === userId);

export default adminPageReducer;
